import { invokeFunction } from '../api'
import { ApiRequestError, toApiRequestError } from '../errors'
import { calculateVisitScore } from './scoring'
import type { ViewerContext } from '../../context/AuthContext'

export type ReportStatus = 'draft' | 'released'
export type ReportPdfGenerationStatus = 'not_generated' | 'generated' | 'failed'
export type ViewerRole = ViewerContext['role']

export interface ReportSummary {
  report_id: string
  visit_id: string
  version: number
  status: ReportStatus
  generated_at: string
  released_at: string | null
  pdf_storage_path: string | null
  pdf_generated_at: string | null
  pdf_generation_status: ReportPdfGenerationStatus
}

export interface ReportDetail extends ReportSummary {
  generated_by: string | null
  released_by: string | null
  report_payload: Record<string, unknown> | null
}

export interface ReportExportResult {
  report_id: string
  redaction_mode: 'redacted' | 'unredacted'
  export_payload: unknown
  export_format?: string
  cms_2567?: unknown
}

function asRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function parseStatus(value: unknown): ReportStatus {
  return value === 'released' ? 'released' : 'draft'
}

function parsePdfGenerationStatus(value: unknown): ReportPdfGenerationStatus {
  if (value === 'generated' || value === 'failed') return value
  return 'not_generated'
}

function toReportSummary(value: unknown): ReportSummary {
  if (!asRecord(value)) throw new Error('Malformed report row in server response')
  if (typeof value.report_id !== 'string') throw new Error('Malformed report row in server response')
  if (typeof value.visit_id !== 'string') throw new Error('Malformed report row in server response')

  return {
    report_id: value.report_id,
    visit_id: value.visit_id,
    version: typeof value.version === 'number' ? value.version : 1,
    status: parseStatus(value.status),
    generated_at: typeof value.generated_at === 'string' ? value.generated_at : new Date(0).toISOString(),
    released_at: typeof value.released_at === 'string' ? value.released_at : null,
    pdf_storage_path: typeof value.pdf_storage_path === 'string' ? value.pdf_storage_path : null,
    pdf_generated_at: typeof value.pdf_generated_at === 'string' ? value.pdf_generated_at : null,
    pdf_generation_status: parsePdfGenerationStatus(value.pdf_generation_status),
  }
}

function toReportDetail(value: unknown): ReportDetail {
  const summary = toReportSummary(value)
  if (!asRecord(value)) throw new Error('Malformed report response from server')

  return {
    ...summary,
    generated_by: typeof value.generated_by === 'string' ? value.generated_by : null,
    released_by: typeof value.released_by === 'string' ? value.released_by : null,
    report_payload: asRecord(value.report_payload) ? value.report_payload : null,
  }
}

export function canReleaseReport(
  role: ViewerRole,
  status: ReportStatus,
  options: { noApprovalPath?: boolean } = {},
): boolean {
  if (status !== 'draft') return false
  if (role === 'Admin') return true
  return role === 'FieldSurveyor' && options.noApprovalPath === true
}

export function canExportUnredacted(role: ViewerRole): boolean {
  return role === 'Admin' || role === 'Reviewer'
}

export function isReportReadOnly(status: ReportStatus): boolean {
  return status === 'released'
}

export function canSendReportPdf(report: Pick<ReportSummary, 'status' | 'pdf_generation_status' | 'pdf_storage_path'>): boolean {
  return report.status === 'released' && report.pdf_generation_status === 'generated' && !!report.pdf_storage_path
}

export async function getReports(): Promise<ReportSummary[]> {
  const { data, error } = await invokeFunction<{ reports: unknown }>('reports', {
    method: 'GET',
    path: '',
  })

  if (error) throw toApiRequestError(error, 'Failed to load reports')
  if (!data || !Array.isArray(data.reports)) throw new Error('Malformed reports response from server')

  return data.reports.map(toReportSummary)
}

export async function getReport(reportId: string): Promise<ReportDetail> {
  const { data, error } = await invokeFunction<{ report: unknown }>('reports', {
    method: 'GET',
    path: `/${reportId}`,
  })

  if (error) throw toApiRequestError(error, 'Failed to load report')
  if (!data) throw new Error('Malformed report response from server')

  return toReportDetail((data as { report?: unknown }).report)
}

export async function generateDraftReport(visitId: string): Promise<ReportSummary> {
  const { data, error } = await invokeFunction<unknown>('reports', {
    method: 'POST',
    path: `/visits/${visitId}/reports:draft`,
  })

  if (error) throw toApiRequestError(error, 'Failed to generate draft report')
  return toReportSummary(data)
}

/**
 * True when scoring calculate refused because the visit already has a finalized score.
 * In that case a scores row already exists — reports:draft can proceed (retry / re-draft).
 */
function isScoreAlreadyFinalizedError(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return false
  if (error.status !== 409) return false
  const msg = String(error.message || '').toLowerCase()
  return msg.includes('finalized') || msg.includes('cannot be recalculated')
}

/**
 * #1799: ensure a scores row exists (via scoring calculate) then generate draft.
 * reports:draft hard-requires scores; calculate was previously orphaned with zero FE callers.
 * H-02: if calculate returns 409 score-finalized, scores already exist — still draft.
 * Auth/tenant failures still fail closed (do not proceed to draft).
 */
export async function calculateScoreAndGenerateDraft(visitId: string): Promise<ReportSummary> {
  try {
    await calculateVisitScore(visitId)
  } catch (error) {
    if (!isScoreAlreadyFinalizedError(error)) {
      throw error
    }
  }
  return generateDraftReport(visitId)
}

export async function generateAiDraftReport(visitId: string): Promise<ReportSummary> {
  const { data, error } = await invokeFunction<unknown>('reports', {
    method: 'POST',
    path: `/visits/${visitId}/reports:ai-draft`,
    timeoutMs: 180_000,
  })

  if (error) throw toApiRequestError(error, 'Failed to generate AI draft report')
  return toReportSummary(data)
}

export async function submitSurveyDraftReport(visitId: string, reportPayload: Record<string, unknown>): Promise<ReportSummary> {
  const { data, error } = await invokeFunction<unknown>('reports', {
    method: 'POST',
    path: `/visits/${visitId}/reports:survey-draft`,
    body: { report_payload: reportPayload },
  })

  if (error) throw toApiRequestError(error, 'Failed to submit draft report')
  return toReportSummary(data)
}

export async function releaseReport(reportId: string): Promise<ReportSummary> {
  const { data, error } = await invokeFunction<unknown>('reports', {
    method: 'POST',
    path: `/reports/${reportId}:release`,
  })

  if (error) throw toApiRequestError(error, 'Failed to release report')
  return toReportSummary(data)
}

export async function exportReport(
  reportId: string,
  options: {
    unredacted?: boolean
    approvalReference?: string
    format?: 'standard' | 'cms-2567'
    branded?: boolean
  } = {},
): Promise<ReportExportResult> {
  const { data, error } = await invokeFunction<{
    report_id: string
    redaction_mode: 'redacted' | 'unredacted'
    export_format?: string
    export_payload: unknown
    cms_2567?: unknown
  }>('reports', {
    method: 'POST',
    path: `/reports/${reportId}:export`,
    body: {
      unredacted: options.unredacted === true,
      approval_reference: options.approvalReference || null,
      ...(options.format ? { format: options.format } : {}),
      ...(options.branded ? { branded: true } : {}),
    },
  })

  if (error) throw toApiRequestError(error, 'Failed to export report')
  if (!data || typeof data.report_id !== 'string') throw new Error('Malformed report export response from server')
  return data
}

export function buildReportExportFilename(
  reportId: string,
  options: {
    redactionMode: 'redacted' | 'unredacted'
    format?: string
    branded?: boolean
  },
): string {
  const formatLabel = options.branded
    ? 'branded'
    : options.format === 'cms-2567'
      ? 'cms-2567'
      : 'standard'
  return `report-${reportId}-${formatLabel}-${options.redactionMode}.json`
}

export function triggerReportExportDownload(
  result: ReportExportResult,
  options: {
    format?: string
    branded?: boolean
  } = {},
): string {
  const downloadPayload = {
    report_id: result.report_id,
    redaction_mode: result.redaction_mode,
    export_format: result.export_format || options.format || 'standard',
    branded: options.branded === true,
    export_payload: result.export_payload,
    ...(typeof result.cms_2567 !== 'undefined' ? { cms_2567: result.cms_2567 } : {}),
  }

  const filename = buildReportExportFilename(result.report_id, {
    redactionMode: result.redaction_mode,
    format: downloadPayload.export_format,
    branded: options.branded,
  })

  const blob = new Blob([JSON.stringify(downloadPayload, null, 2)], {
    type: 'application/json',
  })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  setTimeout(() => URL.revokeObjectURL(url), 0)
  return filename
}
